'use client';
import useMissingSelectionValuesContext from '@susu/custom-made/providers/hooks/useMissingSelectionValuesContext';
import useNavigationContext from '@susu/custom-made/providers/hooks/useNavigationContext';
import useOptionContext from '@susu/custom-made/providers/hooks/useOptionContext';
import useUserContext from '@susu/custom-made/providers/hooks/useUserContext';
import { Level } from '@susu/custom-made/utils/constants';
import type { Option } from '@susu/custom-made/utils/interfaces';
import { getConfigurationTypeBySection, getProductSections, Section } from '@susu/custom-made/utils/navigation';
import { getSectionsWithMissingValues } from '@susu/custom-made/utils/preloadConfiguration/preloadConfiguration';
import { PREVIEW_HEADER_TEST_IDS, SECTION_CONTAINER_TEST_IDS } from '@susu/custom-made/utils/testIds';
import { getConfigurationTypeKey, isExtraConfigurationSection } from '@susu/custom-made/utils/utils';
import { useI18N } from '@susu/i18n/client';

import Button, { ButtonSize, ButtonType } from '../button/button';
import type { Item } from '../navigation/navigation';
import Navigation from '../navigation/navigation';

export const SectionNavigation = ({
  currentSection,
  onItemClick,
  onFinishClick,
}: Readonly<{
  currentSection: string;
  onItemClick: (item: Item | Option) => void;
  onFinishClick: () => void;
}>) => {
  const { t } = useI18N();
  const { product } = useUserContext();
  const { missingOptionValuesPerConfig, missingMonogramValuesPerConfig, missingOptionsPerConfig } =
    useMissingSelectionValuesContext();
  const sectionsWithMissingValues = new Set(
    getSectionsWithMissingValues(missingOptionValuesPerConfig, missingMonogramValuesPerConfig, missingOptionsPerConfig),
  );

  return (
    <nav>
      <Navigation
        items={getProductSections(product!).map(section => ({
          text: t(`navigation.${section.text.toLowerCase()}`),
          id: section.slug,
          isActive: currentSection === section.slug,
          hasMissingValues: sectionsWithMissingValues.has(section.slug),
        }))}
        testId={SECTION_CONTAINER_TEST_IDS.SECTION_NAV}
        onItemClick={onItemClick}
        onFinishClick={onFinishClick}
        showFinishButton={true}
      />
    </nav>
  );
};

export const ExtraProductHeader = ({
  currentSection,
  onFinishClick,
}: Readonly<{
  currentSection: string;
  onFinishClick: () => void;
}>) => {
  const { t } = useI18N();
  const { level, isFinishButtonDisabled } = useNavigationContext();
  const { isBaseStyleSelection } = useOptionContext();

  if (!isExtraConfigurationSection(currentSection as Section) || level !== Level.Group || isBaseStyleSelection) {
    return null;
  }

  return (
    <div className="flex justify-center items-center py-[17px] md:py-24 md:px-20 lg:justify-between lg:pt-28 lg:px-20 lg:pb-30">
      <div
        className="text-center text-sm font-medium leading-sm wrap-break-word md:text-lg md:leading-[26px] lg:text-left lg:text-base lg:leading-base 2xl:text-xl 2xl:leading-lg"
        data-testid={SECTION_CONTAINER_TEST_IDS.EXTRA_PRODUCT_HEADER}
      >
        {t(`configurationType.${getConfigurationTypeKey(getConfigurationTypeBySection(currentSection))}`)}
      </div>
      <div className="hidden lg:block">
        <Button
          onClick={onFinishClick}
          testId={PREVIEW_HEADER_TEST_IDS.SIDE_PANEL_SAVE_BTN}
          data-dd-action-name="Finish"
          isDisabled={isFinishButtonDisabled}
          type={ButtonType.Primary}
          size={ButtonSize.Large}
          noBorder={true}
        >
          {t('previewHeader.finish')}
        </Button>
      </div>
    </div>
  );
};
